import systemArchitectureSourcePaths from "./systemArchitectureSourcePaths.json";

export interface SystemArchitectureDiagramDefinition {
  id: string;
  level: string;
  loadSource: () => Promise<string>;
  owner: string;
  readingKey: string;
  sources: readonly string[];
  statusLabel: string | null;
  summary: string;
  system: string;
  technicalNote: string;
  testingFocus: string;
  testingSources: readonly string[];
  textAlternative: string;
  title: string;
  doesNotProve: string;
  verifiedDate: `${number}-${number}-${number}`;
}

const loadRawSource = async (
  loader: () => Promise<{ default: string }>,
): Promise<string> => (await loader()).default;

export const SYSTEM_ARCHITECTURE_DIAGRAMS = [
  {
    id: "platform-context",
    level: "Big picture",
    loadSource: () =>
      loadRawSource(() => import("./diagrams/platform-context.mmd?raw")),
    owner: "HelloCashflow engineering",
    readingKey:
      "Read from the people at the top, through HelloCashflow, to the connected services below.",
    ...systemArchitectureSourcePaths.diagrams["platform-context"],
    statusLabel: null,
    summary:
      "See who uses HelloCashflow and which outside services connect to it.",
    system: "HelloCashflow platform",
    technicalNote:
      "Technical view: C4 system context. Firebase provides identity and sign-in. Xero, QuickBooks, and Sage are the accounting-provider boundaries. This view intentionally leaves out internal components.",
    testingFocus:
      "Check that each group of people and each outside service has a clear connection to HelloCashflow.",
    textAlternative:
      "Customers use HelloCashflow to manage cash flow, while staff use it to support customers. HelloCashflow uses Firebase for secure sign-in and connects to Xero, QuickBooks, and Sage for accounting data.",
    title: "Platform overview",
    doesNotProve:
      "This view does not prove live availability, provider health, or customer-specific data flows.",
    verifiedDate: "2026-07-16",
  },
  {
    id: "runtime-containers",
    level: "Running apps and services",
    loadSource: () =>
      loadRawSource(() => import("./diagrams/runtime-containers.mmd?raw")),
    owner: "HelloCashflow engineering",
    readingKey:
      "Start with shared code and the two apps, then follow requests through the Node API to stored data and accounting services.",
    ...systemArchitectureSourcePaths.diagrams["runtime-containers"],
    statusLabel: "Configured off by default",
    summary:
      "See how the customer and staff apps reach the Node API that handles requests and accounting data.",
    system: "HelloCashflow runtime",
    technicalNote:
      "Technical view: C4 container. Vite builds the Web and Admin files, Nginx serves them on port 3000, and the Node application programming interface (API) listens on port 8081. Shared packages are built into the apps rather than deployed separately. The Worker service is checked in with a default desired count of zero; this does not prove its live state.",
    testingFocus:
      "Check which parts run separately, which network port each service uses, and which shared code is built into an app.",
    textAlternative:
      "Customers use the Web app and staff use the Admin app. Both send secure requests to the Node API, which stores application data in PostgreSQL and exchanges accounting data with Xero, QuickBooks, and Sage.",
    title: "How the apps run",
    doesNotProve:
      "This view does not show whether live services are healthy, how they scale, whether the network can reach them, or whether the Worker is running now.",
    verifiedDate: "2026-07-16",
  },
  {
    id: "accounting-sync",
    level: "Accounting data flow",
    loadSource: () =>
      loadRawSource(() => import("./diagrams/accounting-sync.mmd?raw")),
    owner: "Accounting sync engineering",
    readingKey:
      "Start with an authorised sync request, then follow provider data, saved recovery state, and progress back to the apps.",
    ...systemArchitectureSourcePaths.diagrams["accounting-sync"],
    statusLabel: null,
    summary:
      "See how HelloCashflow authorises an accounting sync, processes the data, and reports or recovers progress.",
    system: "Accounting data sync",
    technicalNote:
      "Technical view: C4 component. The sync uses fetch-based Server-Sent Events (SSE) for live progress, with polling and stored snapshots as recovery paths. PostgreSQL stores snapshots, claims, and advisory locks. Provider adapters connect to Xero, QuickBooks, or Sage.",
    testingFocus:
      "Check the authorisation step, the single continuous range of months, results grouped by year, progress updates, recovery path, and selected accounting provider.",
    textAlternative:
      "The Web or Admin app starts a sync after HelloCashflow checks the person's identity and organisation access. The sync service processes accounting data, stores recovery records, and sends progress back to both apps.",
    title: "How accounting data syncs",
    doesNotProve:
      "This view does not show whether every provider is fully covered, how fast a live sync runs, or whether the accounting data is correct.",
    verifiedDate: "2026-07-16",
  },
  {
    id: "admin-authorization",
    level: "Access and security",
    loadSource: () =>
      loadRawSource(() => import("./diagrams/admin-authorization.mmd?raw")),
    owner: "Admin platform engineering",
    readingKey:
      "Start with Firebase sign-in, then compare this catalogue's route check with other protected work.",
    ...systemArchitectureSourcePaths.diagrams["admin-authorization"],
    statusLabel: null,
    summary:
      "See how staff sign in, open this catalogue, and pass separate checks for protected work.",
    system: "Admin authorisation",
    technicalNote:
      "Firebase supplies the global Admin claim. The catalogue route requires the existing admin.v2.access permission; every current Admin role includes it, so the catalogue adds no diagram-specific permission. The Admin v2 foundations backend checks active staff before returning permission data, and a temporary feature-flagged backend bypass can waive a missing baseline or server permission for an Admin-claim user. Approved local and end-to-end test modes have separate bypass controls. Other protected API requests repeat server-side permission and organisation-access checks where required.",
    testingFocus:
      "Check the global Admin claim, this catalogue's admin.v2.access route check, access for every current Admin role, other route permissions, deny rules, approved test modes, and server-side checks separately.",
    textAlternative:
      "Staff sign in through Firebase. The architecture catalogue route uses the existing admin.v2.access permission, which every current Admin role includes, without adding another diagram permission. Other protected work keeps its own route and server-side checks. These diagrams are static files with sensitive details removed; access controls do not make bundled files confidential storage.",
    title: "Admin authorisation",
    doesNotProve:
      "This view does not show which staff currently have access, current feature-flag settings, or access to a specific record.",
    verifiedDate: "2026-07-16",
  },
  {
    id: "production-deployment",
    level: "Production setup",
    loadSource: () =>
      loadRawSource(() => import("./diagrams/production-deployment.mmd?raw")),
    owner: "HelloCashflow platform engineering",
    readingKey:
      "Follow the release and user requests into Amazon ECS, then to private apps, data, and platform services.",
    ...systemArchitectureSourcePaths.diagrams["production-deployment"],
    statusLabel: "Configured off by default",
    summary:
      "See how the configuration stored with the code deploys and serves HelloCashflow in production.",
    system: "Configured production deployment",
    technicalNote:
      "GitHub Actions uses OpenID Connect to assume an AWS deploy role. Checked-in policy covers Web, Admin, and API image publication to Amazon Elastic Container Registry (ECR) and Amazon Elastic Container Service (ECS) task deployment on Fargate. The Worker workflow and service exist with a default desired count of zero, but the checked-in Worker image-publication path is not complete; neither fact proves live state. An Application Load Balancer routes requests to private Web, Admin, and API services.",
    testingFocus:
      "Compare the deployment, app services, network, database, logs, secret loading, and the Worker's default zero desired count with the configuration stored with the code.",
    textAlternative:
      "The release branch starts an approved AWS deployment. Staff and customer requests enter through an AWS load balancer and reach private Web, Admin, and Node API services. Database, network, secret-loading, and logging services support those apps. The Worker is configured with a default desired count of zero, which does not prove whether it is running now.",
    title: "Production deployment",
    doesNotProve:
      "This view does not show whether the live deployment still matches this configuration, or whether its services and network are healthy and reachable.",
    verifiedDate: "2026-07-16",
  },
] as const satisfies readonly SystemArchitectureDiagramDefinition[];

export type SystemArchitectureDiagram =
  (typeof SYSTEM_ARCHITECTURE_DIAGRAMS)[number];

export type SystemArchitectureDiagramId = SystemArchitectureDiagram["id"];

export const SYSTEM_ARCHITECTURE_DIAGRAM_IDS: readonly SystemArchitectureDiagramId[] =
  SYSTEM_ARCHITECTURE_DIAGRAMS.map(({ id }) => id);

export const getSystemArchitectureDiagram = (
  id: SystemArchitectureDiagramId,
): SystemArchitectureDiagram => {
  const diagram = SYSTEM_ARCHITECTURE_DIAGRAMS.find(
    (candidate) => candidate.id === id,
  );

  if (!diagram) {
    throw new Error("Unknown system architecture diagram.");
  }

  return diagram;
};
